import ms from "ms";
import React, { useMemo } from "react";
import styles from "./template-list.module.scss";
import { useTranslation } from "react-i18next";
import { Svg } from "@whatslly/ui-components/v2/BaseElements/Svg";
import { AnchorButton } from "@whatslly/ui-components/v2/Button";
import { SearchInput } from "@whatslly/ui-components/v2/BaseElements/SearchInput";
import type { TemplateType } from "@whatslly/types/GetTemplatesResponse";
import { Tooltip } from "@whatslly/ui-components/Tooltip";
import { TemplatesLoader } from "@whatslly/instant-messaging/react-app/templates-widget/components/TemplatesLoader";
import { useDependencyInjector } from "@whatslly/commons/di";
import orderBy from "lodash/orderBy";
import { observer } from "mobx-react-lite";
import { SvgV2 } from "@whatslly/ui-components/v2/BaseElements/SvgV2";
import classnames from "classnames";
import { TemplatesFilter } from "@whatslly/instant-messaging/react-app/templates-widget/components/TemplatesFilter";
import type { IImTemplatesStore, TemplateStoreData } from "@whatslly/instant-messaging/commons/IImTemplatesStore";
import { ImTemplatesStoreSymbol, formatTemplateMsg } from "@whatslly/instant-messaging/commons/IImTemplatesStore";
export const TemplateMenu: React.FC<{ template: TemplateType; templateEditorUrl: string }> = ({ template, templateEditorUrl }) => {
  const store = useDependencyInjector<IImTemplatesStore>(ImTemplatesStoreSymbol);
  return (
    <div className={styles.menu}>
      <a onClick={() => store.openPreviewMsg(template)}>
        <Svg height={13} iconName="eye" />
      </a>
      {template.isEditable && store.allowEditTemplate && (
        <a target="sfdc" href={`${templateEditorUrl}?tid=${template.Id}`}>
          <Svg height={13} iconName="pencile" />
        </a>
      )}
    </div>
  );
};

function getTemplateIcon(template: TemplateType) {
  if (template.templateFiles?.length !== 1) {
    return "file-document";
  }
  const mimeType = template.templateFiles[0].type;
  if (mimeType.includes("image")) {
    return "image";
  }
  if (mimeType.includes("audio")) {
    return "file-audio";
  }
  if (mimeType.includes("pdf")) {
    return "file-pdf";
  }
  return "file-document";
}

export const TemplatesList: React.FC<{ data: TemplateStoreData }> = observer(({ data }) => {
  const store = useDependencyInjector<IImTemplatesStore>(ImTemplatesStoreSymbol);
  const [isLoading, setIsLoading] = React.useState(false);
  const [search, setSearch] = React.useState("");
  const templates = useMemo(() => {
    data.templates.forEach((template) => (template.isFavorite = store.favoriteTemplates.has(template.Id)));
    const orderedTemplates = orderBy(data.templates, ["isFavorite", "Name"], "desc");
    return orderedTemplates.filter(
      ({ Name, msg }) => Name.toLowerCase().includes(search.toLowerCase()) || msg.toLowerCase().includes(search.toLowerCase())
    );
  }, [data.templates, search, store.favoriteTemplates]);

  const copy = async (template: TemplateType) => {
    setIsLoading(true);
    await store.useTemplate(template);
    setIsLoading(false);
  };

  const { t } = useTranslation();

  return (
    <div style={{ height: store.height }} className={styles.container}>
      <div className={classnames(styles.header, { [styles.headerWithoutButton]: !(store.allowCreateTemplate && store.allowEditTemplate) })}>
        <div className={styles.inputWrapper}>
          <SearchInput
            placeholder={t("templates.search")}
            clearAllContainerClassName={styles.clearAllContainer}
            value={search}
            onChange={setSearch}
            className={styles.searchInput}
          />
          <TemplatesFilter />
        </div>
        {store.allowCreateTemplate && store.allowEditTemplate && (
          <AnchorButton
            secondary
            borderRadius="default"
            paddingInline="small"
            width="fit-content"
            href={data.templatesUrl}
            target="sfdc"
            className={styles.manageTemplatesButton}
          >
            {t("templates.manage_templates")}
          </AnchorButton>
        )}
        <Svg iconName="close" className={styles.close} onClick={store.closeTemplateWidget} width={14} height={14} />
      </div>
      <div className={styles.scroll}>
        {!templates.length && data.templates.length ? (
          <div className={styles.noTemplatesFound}>
            <h2>{t("templates.no_results")}</h2>
            {store.allowCreateTemplate ? <div>{t("templates.try_again")}</div> : <div>{t("templates.try_again_without_create")}</div>}
            {store.allowCreateTemplate && (
              <AnchorButton className={styles.noTemplatesFoundButton} secondary borderRadius="default" href={data.templateEditorUrl} target="sfdc">
                <Svg className={styles.plusIcon} iconName="plus" width={13} height={13} />
                {t("templates.create_new")}
              </AnchorButton>
            )}
          </div>
        ) : isLoading ? (
          <TemplatesLoader />
        ) : (
          <div className={styles.list}>
            {templates.map((template) => {
              return (
                <React.Fragment key={template.Id}>
                  <Tooltip delay={ms("1sec")} value={<span>{template.Name}</span>}>
                    <div className={styles.favoriteAndNameContainer}>
                      {store.user?.showFavoriteTemplates && (
                        <div className={styles.favoriteStar} onClick={() => store.toggleFavoriteTemplate(template.Id)}>
                          <SvgV2 iconName="star" height={14} width={15} className={classnames({ [styles.isFavorite]: template.isFavorite })} />
                        </div>
                      )}
                      <div className={styles.templateName}>{template.Name}</div>
                    </div>
                  </Tooltip>
                  <Tooltip
                    placement="right-top"
                    className={styles.menuTooltip}
                    value={<TemplateMenu template={template} templateEditorUrl={data.templateEditorUrl} />}
                  >
                    <div onClick={() => copy(template)} className={styles.templateTextContainer} data-testid={`template-${template.Id}`}>
                      <div className={styles.template}>{formatTemplateMsg(template.msg)}</div>
                      {template.templateFiles?.length ? (
                        <div className={styles.imageCount}>
                          <Svg iconName={getTemplateIcon(template)} width={13} height={13} />
                          <span>{template.templateFiles.length}</span>
                        </div>
                      ) : null}
                    </div>
                  </Tooltip>
                </React.Fragment>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
});
